import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Button, Card, Form, Icon, Modal, TextLink } from '@upside/flux-ui';

import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { clsx } from 'clsx';
import { AppContext } from 'context/App';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { InputOTP, InputOTPSlot } from './InputOTP';
import { TIMEOUT_MINUTES } from './const';
import { useSetTimeoutInMinutes } from './hooks/useSetTimeoutInMinutes';

const defaultCode = '';

export function TotpVerify({
  handleCancel,
  handleTimeout,
  totpState,
}: {
  handleCancel: () => void;
  handleTimeout: () => void;
  totpState?: {
    sessionId: string;
    isSetup: boolean;
  };
}) {
  const context = useContext(AppContext);

  const [modalOpen, setModalOpen] = useState(false);
  useSetTimeoutInMinutes(handleTimeout);

  const { sessionId, isSetup } = totpState || { sessionId: '', isSetup: false };

  const { t: translate } = useTranslation('totp');

  const formSchema = z.object({
    code: z.string(),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: defaultCode,
    },
  });

  const { setValue, setError, formState, handleSubmit, watch } = form;

  const { isSubmitting } = formState;

  const codeValue = watch('code');

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = useCallback(
    async ({ code }) => {
      try {
        const result =
          context &&
          context.authentication.verifyTotpChallenge &&
          (await context.authentication.verifyTotpChallenge({
            mfaSessionId: sessionId,
            userCode: code,
            isSetup,
          }));

        if (result?.status !== 200) {
          setValue('code', defaultCode);
          setError('code', {
            message: translate('error'),
          });
        }
      } catch (error) {
        setValue('code', defaultCode);
        // Display specific error message from API if available
        const errorMessage =
          error instanceof Error ? error.message : translate('error');
        setError('code', {
          message: errorMessage,
        });
      }
    },
    [context, sessionId, setError, setValue, translate, isSetup],
  );

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    // Auto-submit when exactly 6 digits entered (no Zod validation needed)
    if (codeValue && codeValue.length === 6) {
      formRef.current?.requestSubmit();
    }
  }, [codeValue]);

  return (
    <div
      className="my-auto flex w-full flex-col items-center gap-8"
      data-testid="totp-verify"
    >
      <Card
        className={clsx(
          'relative flex w-full max-w-[400px] grow flex-col items-center gap-6 rounded-xl p-8 shadow-md',
          isSubmitting && 'h-96',
        )}
      >
        {isSubmitting && (
          <Icon
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-active-foreground"
            name="spinner"
            spin
            icon={faSpinnerThird}
            size="2xl"
          />
        )}
        {!isSubmitting && (
          <>
            <h1 className="text-center text-2xl font-medium">
              {translate('title')}
            </h1>
            <Form {...form}>
              <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
                <Form.Field
                  control={form.control}
                  name="code"
                  render={({ field }) => (
                    <Form.Item className="flex flex-col items-center space-y-6">
                      <Form.Label>
                        <h3 className="text-center">
                          {translate('enterCode')}
                        </h3>
                      </Form.Label>
                      <Form.Control>
                        <InputOTP autoFocus maxLength={6} {...field}>
                          {new Array(6).fill(0).map((_, index) => (
                            <InputOTPSlot
                              // eslint-disable-next-line react/no-array-index-key
                              key={`otp-field-${index}`}
                              index={index}
                            />
                          ))}
                        </InputOTP>
                      </Form.Control>
                      <Form.Message />
                    </Form.Item>
                  )}
                />
              </form>
            </Form>
            <span className="text-center text-sm text-foreground-secondary">
              {translate('expiresIn', { minutes: TIMEOUT_MINUTES })}
            </span>
            <TextLink onClick={() => setModalOpen(true)}>
              <TextLink.Label>{translate('troubleCode')}</TextLink.Label>
            </TextLink>
          </>
        )}
      </Card>
      <TextLink onClick={handleCancel} type="button" className="z-0">
        <TextLink.Label>{translate('cancel')}</TextLink.Label>
      </TextLink>
      <Modal open={modalOpen}>
        <Modal.Overlay />
        <Modal.Content>
          <Card className="flex max-w-[400px] flex-col items-center gap-6 p-10">
            <div className="grid gap-4">
              <h2 className="text-center text-2xl font-medium">
                {translate('codeNotReceived')}
              </h2>
              <p className="text-center">{translate('requestNewSession')}</p>
            </div>
            <Button className="px-8" size="lg" onClick={handleCancel}>
              {translate('goToLogin')}
            </Button>
          </Card>
        </Modal.Content>
      </Modal>
    </div>
  );
}
